import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { tap, switchMap, catchError, map } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';

import {
  IEmailConfirmResponse,
  IConfirmPhoneResponse,
  ICreatePasswordResponse,
  IConfirmPhoneRequest,
  IConfirmEmailRequest,
  ICreatePasswordRequest,
  ISnActivationError,
} from './activate.interfaces';

@Injectable()
export class ActivateService implements OnDestroy {
  get activationIsFail$(): Observable<ISnActivationError> {
    return this._activationIsFail$.asObservable();
  }

  get mobilePhone$(): Observable<string> {
    return this._mobilePhone$.asObservable();
  }
  public rules: string[];

  private _activationIsFail$ = new Subject<ISnActivationError>();

  private _email: string;
  private _flowId: string;
  private _error: ISnActivationError;

  private readonly _mobilePhone$ = new BehaviorSubject<string>('');

  constructor(private readonly _httpClient: HttpClient) {}

  public ngOnDestroy(): void {
    this._mobilePhone$.complete();
  }

  public confirmEmail(
    uid: string,
    token: string,
  ): Observable<IEmailConfirmResponse | string[] | any> {
    const request: IConfirmEmailRequest = { uid, token };

    return this._httpClient
      .post<IEmailConfirmResponse>('/api/auth/activation/confirm-email/', request)
      .pipe(
        tap({
          next: (response) => {
            this._email = response.email;
            this._flowId = response.flowId;
            this._mobilePhone$.next(response.mobilePhone);

            if (this._error) {
              this._activationIsFail$.next(null);
            }
          },
          error: () => {
            this._error = { email: true };
            this._activationIsFail$.next(this._error);
          },
        }),
        switchMap((response) => {
          return this._httpClient
            .get<string[]>('/api/auth/password/rules/', {
              params: {
                email: response.email,
              },
              headers: {
                'X-fs-skip-error': 'true',
              },
            })
            .pipe(
              catchError(() => of(response)),
              tap((results: string[]) => {
                if (results) {
                  this.rules = results;
                }
              }),
              map((res) => {
                return response;
              }),
            );
        }),
      );
  }
  public confirmPhone(verificationCode: string): Observable<IConfirmPhoneResponse> {
    const request: IConfirmPhoneRequest = {
      flowId: this._flowId,
      email: this._email,
      verificationCode,
    };

    return this._httpClient
      .post<IConfirmPhoneResponse>('/api/auth/activation/confirm-phone/', request)
      .pipe(
        tap({
          next: () => {
            if (this._error) {
              this._activationIsFail$.next(null);
            }
          },
          error: () => {
            this._error = { phone: true };
            this._activationIsFail$.next(this._error);
          },
        }),
      );
  }

  public createPassword(newPassword: string): Observable<ICreatePasswordResponse> {
    const request: ICreatePasswordRequest = {
      flowId: this._flowId,
      email: this._email,
      newPassword,
    };

    return this._httpClient
      .post<ICreatePasswordResponse>('/api/auth/activation/create-password/', request)
      .pipe(
        tap({
          next: () => {
            if (this._error) {
              this._activationIsFail$.next(null);
            }
          },
          error: () => {
            this._error = { password: true };
            this._activationIsFail$.next(this._error);
          },
        }),
      );
  }
}
